import { useEffect, useRef, useState } from 'react';
import { GlobalHeader } from 'br2s-ui-componentlibrary_r2_02';
import 'br2s-ui-componentlibrary_r2_02/dist/foundation/foundation.scss';
import { RootState, useStore } from '@/redux/store';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HEADER_URLS,
  MFE,
  removeRepeatingForwardSlashes,
} from '@/utilities/constants';
import MegaMenu from '../MegaMenu/MegaMenu';
import LogoutModal from '../LogoutSessionModal/LogoutSessionModal';
import InboxTimer from '@/utilities/hooks/InboxTimer';
import {
  hasNoProfilesToSwitch,
  transformToProfileEntitiesObject,
} from '@/pages/ProfileSelection/profileSelection-utilities';
import './Header.scss';
import { useHTTPError, LocalStorageService } from 'br2s-ui-common-utils-02';
import MegaMenuContent from '../MegaMenu/MegaMenuContent';
import Login from '@/components/Login/Login';
import CartTimer from '@/utilities/hooks/CartTimer';
import { cartAPI } from '@/services/api/cart/cartCount';
import NoProfileSwitchModal from '../NoProfileSwitchModal/NoProfileSwitchModal';
import { useCookies } from 'react-cookie';
import { limitString, shouldRenderComponent } from '@/utilities/utilities';
import useNavigateGuard from '@/utilities/hooks/useNavigateGuard';
import { AccountAPI } from '@/services/api/accounts/accounts';
import { getHeaderDropdownButtons, showProfileIndicator } from './headerUtils';
import { TodoAPI } from '@/services/api/todo/todo';
import useProfileSwitch from '@/utilities/hooks/useProfileSwitch/useProfileSwitch';
import { ProfileEntitiesObject } from '@/redux/slices/profileSwitchSlice';
import ProfileIndicator from '../ProfileIndicator/ProfileIndicator';

export type ClickEvt = Parameters<Document['addEventListener']>[1];

interface ICartCount {
  productCount: number;
  subscriptionCount: number;
}

interface HeaderProps {
  isSearchOpen: boolean;
  toggleSearch: () => void;
}

const Header = ({ isSearchOpen, toggleSearch }: HeaderProps) => {
  const { auth, setHasProfile, setHasCspProfile, toggleLoading } = useStore();
  const {
    fetched: switchProfileFetched,
    loading,
    entities,
    fetchProfiles,
  } = useProfileSwitch();
  const {
    uen,
    uenName,
    origin,
    cspIndicator,
    gcIndicator,
    ipIndicator,
    orIndicator,
    roles,
  } = LocalStorageService.getAuthInfo(
    'uen',
    'uenName',
    'origin',
    'cspIndicator',
    'gcIndicator',
    'ipIndicator',
    'orIndicator',
    'roles',
  );

  const [isNoticeHidden, setIsNoticeHidden] = useState(true);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [modalType, setModalType] = useState('');
  const [inboxCount, setInboxCount] = useState(0);
  const [cookies, setCookie] = useCookies(['sessionType', 'isSessionActive']);
  const [loginModal, setLoginModal] = useState(false);
  const [cartCount, setCartCount] = useState<ICartCount>({
    productCount: 0,
    subscriptionCount: 0,
  });

  const currentNotificationCount = useSelector(
    (state: RootState) => state?.app.notification.count,
  );
  const cartPollCount = useRef<number>(0);
  const cartTimeout = useRef<number | null>(null);
  const { handleHTTPError, closeError } = useHTTPError();
  const location = useLocation();
  const inboxPathname = `/${MFE.COMMON}/${HEADER_URLS.INBOX_LISTING}`;
  const inboxPageUrl = removeRepeatingForwardSlashes(
    `${process.env.REACT_APP_BROWSER_ROUTER_BASENAME}/${inboxPathname}`,
  );
  const renderHeaderButtons = !shouldRenderComponent(location);
  const guardedNavigate = useNavigateGuard();

  useEffect(() => {
    toggleLoading(loading && !location.pathname.includes('profile-selection'));
  }, [loading, location.pathname]);

  // Check if user has access to my profile page -- only when authenticated
  useEffect(() => {
    if (auth.authenticated && auth.hasProfileAccess === undefined) {
      (async () => {
        try {
          const response = await AccountAPI.getUserProfile();
          setHasProfile(!!response.data?.result?.peopleProfiles?.length);
        } catch (error) {
          console.error(error);
          handleHTTPError(error, { errorType: 'none' });
          setHasProfile(false);
        }
      })();
    }
  }, [auth.authenticated, auth.hasProfileAccess]);

  useEffect(() => {
    if (auth.authenticated) {
      if (origin !== 'CP') {
        setHasCspProfile(false);
      } else {
        (async () => {
          try {
            const response = await TodoAPI.profile_details(uen);
            const { result } = response.data;
            const hasNonEmptyValue =
              result && Object.keys(result).some((key) => result[key]);
            setHasCspProfile(hasNonEmptyValue);
          } catch (error) {
            console.error(error);
            handleHTTPError(error, { errorType: 'none' });
            setHasCspProfile(false);
          }
        })();
      }
    }
  }, [auth.authenticated]);

  useEffect(() => {
    setInboxCount(currentNotificationCount);
  }, [currentNotificationCount]);

  const navigate = useNavigate();
  // Holds previous count values for cart to use inside of the event listener.
  const cartCountsRef = useRef<ICartCount>({
    productCount: 0,
    subscriptionCount: 0,
  });
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    // Add style to body if mega menu is open
    document.body.classList[isMegaMenuOpen ? 'add' : 'remove'](
      'mega-menu-open',
    );
  }, [isMegaMenuOpen]);

  useEffect(() => {
    const openModal = () => {
      setLoginModal(!cookies.isSessionActive);
    };

    window.addEventListener('openLoginModal', openModal);
    return () => {
      window.removeEventListener('openLoginModal', openModal);
    };
  }, [location, location.pathname]);

  const openLoginModal = () => {
    LocalStorageService.clearUrlPriorToLogin();
    setLoginModal(true);
  };

  const headerActionEvents: Record<string, () => void> = {
    DASHBOARD: () => guardedNavigate(`/${HEADER_URLS.DASHBOARD}`),
    LOGIN: openLoginModal,
    LOGOUT: () => {
      setShowLogout(true);
      setModalType('logout');
    },
    TOGGLE_MEGAMENU: () => setIsMegaMenuOpen((v) => !v),
    INBOX: () => {
      if (location.pathname === inboxPathname) {
        window.location.href = inboxPageUrl;
      }
      guardedNavigate(inboxPathname);
    },
    CART: () => guardedNavigate(`/${HEADER_URLS.CART}`),
    SWITCH_PROFILE: () => {
      !switchProfileFetched
        ? fetchProfiles(handleNavigateToSwitchProfile)
        : handleNavigateToSwitchProfile(
            transformToProfileEntitiesObject(entities),
          );
    },
    PAYMENT_SETTINGS: () => guardedNavigate(`/${HEADER_URLS.PAYMENT_SETTINGS}`),
    PAYMENT_HISTORY: () =>
      guardedNavigate(
        `/${MFE.IPS_API_MARKETPLACE}/${HEADER_URLS.PAYMENT_HISTORY}`,
      ),
    MY_PROFILE: () => guardedNavigate(`/${HEADER_URLS.MY_PROFILE}`),
    TOGGLE_SEARCH: toggleSearch,
    TRANSACTIONS: () => guardedNavigate(`/${HEADER_URLS.MY_TRANSACTION}`),
    ENTITIES: () => guardedNavigate(`/${HEADER_URLS.ENTITIES}`),
    CORPORATE_SERVICE_PROVIDER: () => guardedNavigate(`/${HEADER_URLS.MY_CSP}`),
    MY_CLIENTS: () => guardedNavigate(`/${HEADER_URLS.MY_CLIENTS}`),
  };

  const actionEvent = (action: string) => {
    if (headerActionEvents[action]) {
      return headerActionEvents[action]();
    }
  };

  const noticeBannerProps = {
    id: 'test_noticeBanner',
    isHidden: isNoticeHidden,
    onDismiss: () => setIsNoticeHidden(true),
  };

  useEffect(() => {
    const handleClick: ClickEvt = (e) => {
      const evtTarget = e.target as HTMLElement;
      const { id, className } = evtTarget;
      const isTargetCloseIcon =
        className !== 'icon-sys icon-sys-action-close' &&
        id !== 'global-header-menu';

      if (
        ref.current &&
        !ref.current.contains(evtTarget) &&
        isTargetCloseIcon
      ) {
        setIsMegaMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [ref, isMegaMenuOpen]);

  const handleNavigateToSwitchProfile = (records: ProfileEntitiesObject) => {
    if (hasNoProfilesToSwitch(records)) {
      return setProfileModal(true);
    }

    guardedNavigate('/profile-selection');
  };

  const handleSetCartCount = (counts: ICartCount) => {
    cartCountsRef.current = counts;
    setCartCount(counts);
  };
  const cartUpdated = useRef(false);
  const callCartTimer = () => {
    if (cartTimeout.current) {
      clearTimeout(cartTimeout.current);
    }
    if (cartPollCount.current < 7 && !cartUpdated.current) {
      cartTimeout.current = window.setTimeout(() => {
        callCartAPI();
      }, 1000);
    }
  };

  const callCartAPI = async () => {
    const response = await cartAPI.cartCount();

    if (response?.status === 200) {
      const totalCartCount = {
        productCount: response?.data.result.productCount || 0,
        subscriptionCount: response?.data.result.subscriptionCount || 0,
      };
      if (
        response?.data.result.subscriptionCount !==
          cartCountsRef.current.subscriptionCount ||
        response?.data.result.productCount !==
          cartCountsRef.current.productCount
      ) {
        setCartCount(totalCartCount);
        cartUpdated.current = true;
      }
      cartPollCount.current++;
      callCartTimer();
    }
  };
  // istanbul ignore next
  const pollCartCountOnPaymentSuccess = async () => {
    callCartAPI();
  };

  const handleSetCartCountWithEvent = async (event: Event) => {
    const { detail } = event as CustomEvent<{ cart: Partial<ICartCount> }>;

    const productCount = detail.cart?.productCount;
    const subscriptionCount = detail.cart?.subscriptionCount;

    const counts: ICartCount = {
      ...cartCountsRef.current,
    };

    if (productCount !== undefined) {
      counts.productCount = productCount;
    }

    if (subscriptionCount !== undefined) {
      counts.subscriptionCount = subscriptionCount;
    }

    handleSetCartCount(counts);
  };

  const onLogout = () => {
    // set cart counts to 0 when logout
    // but by right should call callCartAPI()
    handleSetCartCount({
      productCount: 0,
      subscriptionCount: 0,
    });
    if (modalType === 'session') navigate('/sessionTimeout');
    else if (modalType === 'logout') navigate('/logout');
  };

  useEffect(() => {
    document.addEventListener(
      'ips-cart-count-polling',
      pollCartCountOnPaymentSuccess,
    );
    return () => {
      document.removeEventListener(
        'ips-cart-count-polling',
        pollCartCountOnPaymentSuccess,
      );
    };
  }, []);

  useEffect(() => {
    document.addEventListener('ips-cart-count', handleSetCartCountWithEvent);
    return () => {
      document.removeEventListener(
        'ips-cart-count',
        handleSetCartCountWithEvent,
      );
    };
  }, []);

  useEffect(() => {
    const requestLogin = (e: { detail: { url: string } }) => {
      const detail = e.detail;
      const url = detail.url;
      setLoginModal(true);
      LocalStorageService.setUrlPriorToLogin(url);
    };

    document.addEventListener(
      'requestLogin',
      requestLogin as unknown as EventListener,
    );
    return () => {
      document.removeEventListener(
        'requestLogin',
        requestLogin as unknown as EventListener,
      );
    };
  }, []);

  const indicators = {
    cspIndicator,
    gcIndicator,
    ipIndicator,
    orIndicator,
  };
  return (
    <>
      <GlobalHeader
        id="global-header"
        isLoggedIn={auth.authenticated}
        noticeBannerProps={noticeBannerProps}
        actionEvent={actionEvent}
        badgeValues={{
          cart:
            cartCount.productCount + cartCount.subscriptionCount || undefined,
          ...(inboxCount !== 0 && { inbox: inboxCount }),
        }}
        isMegaMenuOpen={isMegaMenuOpen}
        username={uenName ? limitString(uenName as string, 20) : ''}
        onLogoClick={() => {
          closeError();
          guardedNavigate('/');
        }}
        isSearchOpen={isSearchOpen}
        hideHeaderButtons={renderHeaderButtons}
        dropdownButtons={getHeaderDropdownButtons(auth)}
      />

      {auth.authenticated &&
        roles &&
        showProfileIndicator({ roles, origin }) && (
          <ProfileIndicator indicators={indicators} roles={roles} />
        )}

      <MegaMenu
        id="mega-menu"
        isMegaMenuOpen={isMegaMenuOpen}
        closeMegaMenu={() => setIsMegaMenuOpen(false)}
        render={(props) => <MegaMenuContent {...props} />}
        ref={ref}
      />

      {auth.authenticated && (
        <>
          <LogoutModal
            showLogout={showLogout}
            setShowLogout={setShowLogout}
            onLogout={onLogout}
            setModalType={setModalType}
            modalType={modalType}
            setCookie={setCookie}
          />
          <InboxTimer setInboxCount={setInboxCount} />
        </>
      )}
      <CartTimer setCartCount={setCartCount} />
      <NoProfileSwitchModal
        isOpen={profileModal}
        onClick={() => setProfileModal(false)}
        onCloseClick={() => setProfileModal(false)}
      />
      {loginModal && <Login setLoginModal={setLoginModal} />}
    </>
  );
};

export default Header;
